import * as Sentry from '@sentry/browser';
import numeral from 'numeral';
import { action, computed, makeObservable, observable, runInAction } from 'mobx';
import moment from 'moment';

import { ComponentTierName } from '@/constants/enums';
import { checkTierPermission, waitFor } from '@/utils/util-functions';
import epaxios from '@/utils/epaxios';
import { energySavingMockData } from '@/constants/energySavingMockData';

import RootStore from './RootStore';

export default class EnergySavingsStore {
  rootStore: typeof RootStore;
  @observable energySavings = {};
  @observable isReady = false;
  @observable isInErrorState = false;
  @observable requestedYear = moment().year();

  constructor(rootStore: typeof RootStore) {
    makeObservable(this);
    this.rootStore = rootStore;
  }

  @action get = async () => {
    try {
      this.reset();
      const stationId = this.rootStore.stationStore?.currentStation.id;
      await waitFor(() => this.rootStore.stationStore.isLoading === false);

      const hasEnergySavingsPermission = checkTierPermission(
        ComponentTierName.EnergySavingsWidget,
        this.rootStore.stationStore,
      );

      const isWasteWaterTreatment = this.rootStore.stationStore.isWasteWaterStation;
      const apiSuffixWithPermission = 'calculated-gauge';
      const apiSuffixWithoutPermission = this.isHistoricalData ? `historical-gauge/${this.requestedYear}` : 'gauge';

      const data = isWasteWaterTreatment
        ? energySavingMockData
        : (
            await epaxios.get(
              `value-dashboard/energy-saving/station/${stationId}/${
                hasEnergySavingsPermission ? apiSuffixWithPermission : apiSuffixWithoutPermission
              }`,
            )
          )?.data;

      if (data?.validationErrorName) {
        throw new Error(data.msg);
      }

      runInAction(() => {
        this.energySavings =
          hasEnergySavingsPermission && !this.rootStore.stationStore.isWasteWaterStation
            ? new CalculatedEnergySavingsGauge(data)
            : new EnergySavingsGauge(data);
        this.requestedYear = data.requestedYear ?? this.requestedYear;
        this.isReady = true;
      });
    } catch (e) {
      this.isInErrorState = true;
      console.warn(e);
      Sentry.captureException(new Error(`Error when setting Energy Saving Gauge data: ${e}`));
    } finally {
      this.isReady = true;
    }
  };

  @computed get isHistoricalData() {
    return this.requestedYear !== new Date().getFullYear();
  }

  @action reset = () => {
    this.energySavings = {};
    this.isReady = false;
    this.isInErrorState = false;
  };

  @action setRequestedYear = requestedYear => {
    this.requestedYear = requestedYear;
    this.get();
  };
}

class CalculatedEnergySavingsGauge {
  @observable min: EnergySavingsGauge;
  @observable max: EnergySavingsGauge;

  constructor(energySavings: CalculatedEnergySavingsGauge) {
    makeObservable(this);
    this.min = new EnergySavingsGauge(energySavings.min, true);
    this.max = new EnergySavingsGauge(energySavings.max, true);
  }
}

class EnergySavingsGauge {
  @observable yearSavings = {};
  @observable monthSavings = {};
  @observable daySavings = {};
  @observable lifetimeSavings = {};

  constructor(energySavings, isCalculated?: boolean) {
    makeObservable(this);
    if (energySavings.requestedYear || isCalculated) {
      this.yearSavings = new ToDate(energySavings.yearlyAverage);
      this.monthSavings = new ToDate(energySavings.monthlyAverage);
      this.daySavings = new ToDate(energySavings.dailyAverage);
      this.lifetimeSavings = new ToDate(energySavings.lifetime);
    } else {
      this.yearSavings = new ToDate(energySavings.yearToDate);
      this.monthSavings = new ToDate(energySavings.monthToDate);
      this.daySavings = new ToDate(energySavings.yesterday);
      this.lifetimeSavings = new ToDate(energySavings.lifetime);
    }
  }
}

export class ToDate {
  @observable energyKwh;
  @observable specificPowerDeltaRelative;
  @observable savingValue;
  @observable savingCurrency;
  @observable savingCo2;

  constructor(toDate) {
    makeObservable(this);
    this.energyKwh = toDate?.energyKwh;
    this.specificPowerDeltaRelative = toDate?.specificPowerDeltaRelative;
    this.savingValue = toDate?.savingValue;
    this.savingCurrency = toDate?.savingCurrency;
    this.savingCo2 = toDate?.savingCo2;
  }

  @computed get hasValues(): boolean {
    return (
      this.energyKwh !== undefined ||
      this.savingValue !== undefined ||
      this.savingCo2 !== undefined ||
      this.specificPowerDeltaRelative !== undefined
    );
  }

  @computed get percentageFormatted() {
    return this.specificPowerDeltaRelative == null ? 'N/A' : numeral(this.specificPowerDeltaRelative).format('0%');
  }

  @computed get savingText() {
    if (this.savingValue && this.savingCurrency) {
      return `${numeral(this.savingValue).format('0,0')} ${this.savingCurrency}`;
    }

    return `-- ${this.savingCurrency ?? ''}`;
  }

  @computed get energyKwhFormatted() {
    if (!this.energyKwh) return '--';

    return numeral(this.energyKwh).format('0,0');
  }

  @computed get co2SavingFormatted() {
    if (!this.savingCo2) return '--';

    return numeral(this.savingCo2).format(this.savingCo2 > 100 ? '0,0' : '0,0.[0]');
  }

  @computed get kWhToMoneyRate(): number {
    if (!this.energyKwh || !this.savingCurrency) return;

    return this.savingValue / this.energyKwh;
  }

  @computed get kWhToCo2Rate(): number {
    if (!this.energyKwh || !this.savingCo2) return;

    return this.savingCo2 / this.energyKwh;
  }
}
